import type { EditPositionFillRequestCommand } from 'domain/shared/positionFillRequest';
import { POSITION_FILL_REQUEST_STATUS } from '../../constants';
import type { PositionFillRequest, PositionFillRequestListItem } from '../../types';
import usePositionFillRequestsService from '../../usePositionFillRequestsService';
import type { EditPositionFillRequestFormValues } from './types';
import useRecruitmentPoolPositionFillRequestService from 'domain/recruiting/recruitmentPool/useRecruitmentPoolPositionFillRequestService';

const CONTENT_FIELDS = ['responsibilities', 'requirements', 'projectName', 'projectDescription', 'notes'] as const;

const normalizeValue = (value?: string | null) => value ?? '';

const hasPositionFillRequestContentChanges = (
  positionFillRequest: PositionFillRequestListItem,
  values: EditPositionFillRequestFormValues,
) => CONTENT_FIELDS.some(field => normalizeValue(positionFillRequest[field]) !== normalizeValue(values[field]));

const buildEditCommand = (
  positionFillRequest: PositionFillRequestListItem,
  values: EditPositionFillRequestFormValues,
): EditPositionFillRequestCommand => ({
  ownerAlias: values.ownerAlias,
  expirationDate: positionFillRequest.expirationDate,
  responsibilities: values.responsibilities,
  requirements: values.requirements,
  requiredExperience: positionFillRequest.requiredExperience,
  englishLevel: positionFillRequest.englishLevel,
  projectName: values.projectName ?? undefined,
  projectDescription: values.projectDescription ?? undefined,
  notes: values.notes ?? undefined,
});

const useEditPositionFillRequestService = () => {
  const { editPositionFillRequest } = usePositionFillRequestsService();
  const { attachPositionFillRequest, changePositionFillRequestPool } = useRecruitmentPoolPositionFillRequestService();

  const savePositionFillRequest = async (
    positionFillRequest: PositionFillRequestListItem,
    values: EditPositionFillRequestFormValues,
  ): Promise<PositionFillRequest> => {
    const isActive = positionFillRequest.status === POSITION_FILL_REQUEST_STATUS.ACTIVE;
    const contentChanged = hasPositionFillRequestContentChanges(positionFillRequest, values);
    const ownerChanged = positionFillRequest.ownerAlias !== values.ownerAlias;
    const currentRecruitmentPoolId = positionFillRequest.recruitmentPoolId ?? undefined;
    const nextRecruitmentPoolId = values.recruitmentPoolId ?? undefined;
    const recruitmentPoolChanged = isActive && currentRecruitmentPoolId !== nextRecruitmentPoolId;

    if (!contentChanged && !ownerChanged && !recruitmentPoolChanged) return positionFillRequest;

    if (
      isActive &&
      !contentChanged &&
      !ownerChanged &&
      recruitmentPoolChanged &&
      currentRecruitmentPoolId &&
      nextRecruitmentPoolId
    ) {
      await changePositionFillRequestPool(positionFillRequest.id, currentRecruitmentPoolId, nextRecruitmentPoolId);

      return { ...positionFillRequest, recruitmentPoolId: nextRecruitmentPoolId };
    }

    const updatedPositionFillRequest = await editPositionFillRequest(
      positionFillRequest.id,
      buildEditCommand(positionFillRequest, values),
    );

    if (updatedPositionFillRequest.status === POSITION_FILL_REQUEST_STATUS.ACTIVE) return updatedPositionFillRequest;

    if (
      (ownerChanged || recruitmentPoolChanged) &&
      nextRecruitmentPoolId &&
      updatedPositionFillRequest.status === POSITION_FILL_REQUEST_STATUS.PENDING
    ) {
      await attachPositionFillRequest(nextRecruitmentPoolId, positionFillRequest.id);

      return {
        ...updatedPositionFillRequest,
        status: POSITION_FILL_REQUEST_STATUS.ACTIVE,
        recruitmentPoolId: nextRecruitmentPoolId,
      };
    }

    return {
      ...updatedPositionFillRequest,
      recruitmentPoolId: nextRecruitmentPoolId,
    };
  };

  return { savePositionFillRequest };
};

export default useEditPositionFillRequestService;
